import { useEffect, useState } from "react";
import axios from "axios";
import {
  Upload,
  Download,
  Lock,
  LogOut,
  CheckCircle2,
  AlertCircle,
  RefreshCcw,
  Trash2,
  FileSpreadsheet,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "lenax_admin_passcode";

export default function Admin() {
  const [passcode, setPasscode] = useState("");
  const [authed, setAuthed] = useState(false);
  const [error, setError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  const [stats, setStats] = useState({ products: 0, categories: 0 });
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("replace");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [resultError, setResultError] = useState("");

  // Try existing passcode on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return;
    (async () => {
      try {
        const res = await axios.post(`${API}/admin/verify`, { passcode: saved });
        if (res.data?.ok) {
          setPasscode(saved);
          setAuthed(true);
        }
      } catch {
        localStorage.removeItem(STORAGE_KEY);
      }
    })();
  }, []);

  const refreshStats = async () => {
    try {
      const res = await axios.get(`${API}/catalog/stats`);
      setStats(res.data);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    if (authed) refreshStats();
  }, [authed]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoginLoading(true);
    try {
      const res = await axios.post(`${API}/admin/verify`, { passcode });
      if (res.data?.ok) {
        localStorage.setItem(STORAGE_KEY, passcode);
        setAuthed(true);
      } else {
        setError("Invalid passcode");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid passcode");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setPasscode("");
    setAuthed(false);
    setResult(null);
    setResultError("");
  };

  const handleDownloadTemplate = () => {
    window.open(`${API}/catalog/template`, "_blank");
  };

  const handleDownloadBackup = async () => {
    try {
      const res = await axios.get(`${API}/catalog/backup`, {
        headers: { "X-Admin-Passcode": passcode },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
      a.href = url;
      a.download = `lenax-catalog-backup-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setResultError(err.response?.data?.detail || "Could not download backup");
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setResult(null);
    setResultError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      const res = await axios.post(`${API}/catalog/import`, fd, {
        headers: { "X-Admin-Passcode": passcode },
      });
      setResult(res.data);
      setFile(null);
      // Reset input
      const input = document.getElementById("catalog-file-input");
      if (input) input.value = "";
      await refreshStats();
    } catch (err) {
      setResultError(err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm("Clear the entire product catalog? This cannot be undone.")) {
      return;
    }
    setImporting(true);
    setResult(null);
    setResultError("");
    try {
      await axios.post(
        `${API}/catalog/clear`,
        {},
        { headers: { "X-Admin-Passcode": passcode } },
      );
      await refreshStats();
      setResult({ ok: true, mode: "cleared", products_imported: 0, categories: 0 });
    } catch (err) {
      setResultError(err.response?.data?.detail || "Could not clear catalog");
    } finally {
      setImporting(false);
    }
  };

  // ---------- LOGIN VIEW ----------
  if (!authed) {
    return (
      <div
        data-testid="admin-login-screen"
        className="min-h-[80vh] flex items-center justify-center px-6 py-20"
      >
        <form
          onSubmit={handleLogin}
          className="w-full max-w-md border border-[#E0E5E2] bg-white p-10"
        >
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#F1F3F0] text-[#1E8A2E]">
            <Lock size={18} />
          </div>
          <p className="overline text-[#7A827D] mt-6">Restricted area</p>
          <h1 className="font-serif text-4xl mt-3 text-[#121413] tracking-tight">
            Catalog admin
          </h1>
          <p className="mt-3 text-sm text-[#4A524D]">
            Enter your admin passcode to manage the product catalog.
          </p>

          <label className="overline text-[#7A827D] block mt-8 mb-2">
            Passcode
          </label>
          <input
            type="password"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            data-testid="admin-passcode-input"
            placeholder="••••••••"
            className="w-full px-4 py-3 bg-white border border-[#E0E5E2] text-sm focus:outline-none focus:border-[#1E8A2E] rounded-sm"
            autoFocus
          />
          {error && (
            <p
              data-testid="admin-login-error"
              className="mt-3 text-sm text-[#D92D2D] flex items-center gap-2"
            >
              <AlertCircle size={14} /> {error}
            </p>
          )}
          <button
            type="submit"
            data-testid="admin-login-button"
            disabled={loginLoading || !passcode}
            className="mt-8 w-full inline-flex items-center justify-center px-6 py-3.5 bg-[#121413] text-white text-sm font-medium hover:bg-[#D92D2D] disabled:opacity-50 transition-colors duration-300 rounded-sm"
          >
            {loginLoading ? "Verifying…" : "Sign in"}
          </button>
        </form>
      </div>
    );
  }

  // ---------- ADMIN DASHBOARD ----------
  return (
    <div data-testid="admin-dashboard" className="bg-[#F8F9F7] min-h-[80vh]">
      <section className="border-b border-[#E0E5E2] py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12 flex items-end justify-between gap-6 flex-wrap">
          <div>
            <p className="overline text-[#1E8A2E]">Catalog admin</p>
            <h1 className="font-serif text-4xl md:text-5xl mt-3 text-[#121413] tracking-tight">
              Manage your product catalog
            </h1>
            <p className="mt-3 text-sm text-[#4A524D] max-w-xl">
              Upload a CSV or Excel sheet to update the products that appear on
              your public Products page.
            </p>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#4A524D] hover:text-[#D92D2D] transition-colors"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#E0E5E2] border border-[#E0E5E2]">
          {/* STATS */}
          <div className="bg-white p-8" data-testid="admin-stats">
            <p className="overline text-[#7A827D]">Currently published</p>
            <div className="mt-6 flex items-end gap-4">
              <p className="font-serif text-6xl text-[#121413] tracking-tight">
                {stats.products}
              </p>
              <p className="text-sm text-[#4A524D] mb-2">products</p>
            </div>
            <p className="mt-2 text-sm text-[#4A524D]">
              across <span className="text-[#1E8A2E] font-semibold">{stats.categories}</span> categories
            </p>
            <button
              onClick={refreshStats}
              className="mt-6 inline-flex items-center gap-2 text-xs font-medium text-[#121413] hover:text-[#1E8A2E] transition-colors"
            >
              <RefreshCcw size={12} /> Refresh
            </button>
            <button
              onClick={handleDownloadBackup}
              data-testid="admin-download-backup"
              disabled={stats.products === 0}
              className="mt-6 ml-6 inline-flex items-center gap-2 text-xs font-medium text-[#121413] hover:text-[#1E8A2E] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Download size={12} /> Download backup
            </button>
          </div>

          {/* TEMPLATE */}
          <div className="bg-white p-8">
            <p className="overline text-[#7A827D]">Step 1 — Template</p>
            <h3 className="font-serif text-2xl mt-3 text-[#121413]">
              Download CSV template
            </h3>
            <p className="mt-3 text-sm text-[#4A524D]">
              Fill in one row per product. Reuse the same{" "}
              <code className="text-[#1E8A2E]">category_id</code> + name for
              products that share a category.
            </p>
            <button
              onClick={handleDownloadTemplate}
              data-testid="admin-download-template"
              className="mt-6 inline-flex items-center gap-2 border border-[#121413]/20 px-5 py-3 text-sm font-medium text-[#121413] hover:border-[#1E8A2E] hover:text-[#1E8A2E] transition-colors rounded-sm"
            >
              <Download size={14} /> Download template
            </button>
            <details className="mt-6">
              <summary className="text-xs text-[#7A827D] cursor-pointer hover:text-[#121413]">
                Required columns
              </summary>
              <ul className="mt-3 text-xs text-[#4A524D] space-y-1.5 leading-relaxed">
                <li><code className="text-[#1E8A2E]">category_id</code> — short slug, e.g. <em>cardiology</em></li>
                <li><code className="text-[#1E8A2E]">category_name</code> — e.g. <em>Cardiology</em></li>
                <li><code className="text-[#7A827D]">category_description</code> — optional</li>
                <li><code className="text-[#1E8A2E]">name</code> — product brand name</li>
                <li><code className="text-[#1E8A2E]">composition</code></li>
                <li><code className="text-[#1E8A2E]">dosage_form</code> — Tablet / Syrup / Cream…</li>
                <li><code className="text-[#1E8A2E]">indication</code></li>
              </ul>
            </details>
          </div>

          {/* DANGER */}
          <div className="bg-white p-8">
            <p className="overline text-[#D92D2D]">Danger zone</p>
            <h3 className="font-serif text-2xl mt-3 text-[#121413]">
              Clear catalog
            </h3>
            <p className="mt-3 text-sm text-[#4A524D]">
              Removes every product from the database. The site will fall back
              to the built-in sample catalog until you import again.
            </p>
            <button
              onClick={handleClear}
              data-testid="admin-clear-catalog"
              disabled={importing || stats.products === 0}
              className="mt-6 inline-flex items-center gap-2 border border-[#D92D2D]/40 text-[#D92D2D] px-5 py-3 text-sm font-medium hover:bg-[#D92D2D] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors rounded-sm"
            >
              <Trash2 size={14} /> Clear catalog
            </button>
          </div>
        </div>
      </section>

      {/* UPLOAD */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <p className="overline text-[#1E8A2E]">Step 2 — Upload</p>
          <h2 className="font-serif text-4xl md:text-5xl mt-3 text-[#121413] tracking-tight">
            Import your catalog
          </h2>

          <div className="mt-10 border border-[#E0E5E2] bg-white p-8 md:p-10">
            <label
              htmlFor="catalog-file-input"
              className="block border-2 border-dashed border-[#E0E5E2] p-10 text-center cursor-pointer hover:border-[#1E8A2E] hover:bg-[#F1F3F0] transition-colors rounded-sm"
              data-testid="admin-upload-dropzone"
            >
              <FileSpreadsheet
                size={32}
                className="mx-auto text-[#1E8A2E]"
              />
              <p className="mt-4 font-serif text-2xl text-[#121413]">
                {file ? file.name : "Drop your CSV or .xlsx file here"}
              </p>
              <p className="mt-2 text-xs text-[#7A827D]">
                {file
                  ? `${(file.size / 1024).toFixed(1)} KB · Click again to change`
                  : "or click to browse"}
              </p>
              <input
                id="catalog-file-input"
                data-testid="admin-file-input"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
              />
            </label>

            <div className="mt-8 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
              <div>
                <p className="overline text-[#7A827D] mb-3">Mode</p>
                <div className="flex gap-3" data-testid="admin-mode-selector">
                  <label
                    className={`inline-flex items-center gap-2 px-4 py-2.5 border text-sm cursor-pointer transition-colors rounded-sm ${
                      mode === "replace"
                        ? "border-[#1E8A2E] bg-[#F1F3F0] text-[#121413]"
                        : "border-[#E0E5E2] text-[#4A524D] hover:text-[#121413]"
                    }`}
                  >
                    <input
                      type="radio"
                      name="mode"
                      value="replace"
                      checked={mode === "replace"}
                      onChange={() => setMode("replace")}
                      className="accent-[#1E8A2E]"
                    />
                    Replace all
                  </label>
                  <label
                    className={`inline-flex items-center gap-2 px-4 py-2.5 border text-sm cursor-pointer transition-colors rounded-sm ${
                      mode === "append"
                        ? "border-[#1E8A2E] bg-[#F1F3F0] text-[#121413]"
                        : "border-[#E0E5E2] text-[#4A524D] hover:text-[#121413]"
                    }`}
                  >
                    <input
                      type="radio"
                      name="mode"
                      value="append"
                      checked={mode === "append"}
                      onChange={() => setMode("append")}
                      className="accent-[#1E8A2E]"
                    />
                    Append
                  </label>
                </div>
              </div>

              <div className="md:ml-auto">
                <button
                  onClick={handleImport}
                  disabled={!file || importing}
                  data-testid="admin-import-button"
                  className="inline-flex items-center gap-2 bg-[#121413] text-white px-7 py-3.5 text-sm font-medium hover:bg-[#1E8A2E] disabled:opacity-40 disabled:cursor-not-allowed transition-colors rounded-sm"
                >
                  <Upload size={14} />
                  {importing ? "Importing…" : "Import catalog"}
                </button>
              </div>
            </div>

            {result && (
              <div
                data-testid="admin-import-success"
                className="mt-8 border border-[#1E8A2E]/30 bg-[#F1F8F2] p-6 flex items-start gap-4 rounded-sm"
              >
                <CheckCircle2
                  size={20}
                  className="text-[#1E8A2E] mt-0.5 shrink-0"
                />
                <div className="text-sm text-[#121413]">
                  <p className="font-semibold">
                    {result.mode === "cleared"
                      ? "Catalog cleared."
                      : `Imported ${result.products_imported} products across ${result.categories} categories.`}
                  </p>
                  {result.skipped > 0 && (
                    <p className="text-[#7A827D] mt-1">
                      {result.skipped} row(s) skipped (missing name/category or
                      duplicates).
                    </p>
                  )}
                  <p className="text-[#4A524D] mt-2">
                    Visit your{" "}
                    <a
                      href="/products"
                      className="underline text-[#1E8A2E] hover:text-[#121413]"
                    >
                      Products page
                    </a>{" "}
                    to see the changes live.
                  </p>
                </div>
              </div>
            )}

            {resultError && (
              <div
                data-testid="admin-import-error"
                className="mt-8 border border-[#D92D2D]/30 bg-[#FDF1F1] p-6 flex items-start gap-4 rounded-sm"
              >
                <AlertCircle
                  size={20}
                  className="text-[#D92D2D] mt-0.5 shrink-0"
                />
                <div className="text-sm text-[#121413]">
                  <p className="font-semibold">Import failed</p>
                  <p className="text-[#4A524D] mt-1">{resultError}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
